import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Target, Eye, Zap, Award, ArrowUpRight } from "lucide-react";
import SectionHeading from "@/components/shared/SectionHeading";

const PILLARS = [
  { icon: Eye, title: "Reach", text: "2.6 billion monthly impressions across 42 cities, wherever your audience lives, moves, or travels." },
  { icon: Award, title: "Trophy Locations", text: "First-pick highway billboards, airport hubs, premium malls, and metro pillars no one else can offer." },
  { icon: Zap, title: "Data-Driven Planning", text: "Mobility data, footfall analytics, and brand-lift studies tied to every plan we build." },
  { icon: Target, title: "Creative Built for Outdoor", text: "We don't repurpose digital banners. We build six-word ideas that work at 80 mph." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="text-zinc-100">
      <section className="relative pt-36 md:pt-44 pb-24 border-b border-white/5">
        <div className="absolute inset-0 -z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-950/30 via-zinc-950 to-zinc-950" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="font-mono-accent text-[11px] text-amber-400 mb-5">
            About Horizn Ad Media
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-black leading-[0.95] tracking-tighter max-w-5xl">
            Outdoor isn't dying.
            <br />
            <span className="gradient-text italic">It's being rebuilt.</span>
          </h1>
          <p className="mt-8 max-w-3xl text-lg md:text-xl text-zinc-400 leading-relaxed">
            Horizn Ad Media Private Limited is building India's most ambitious
            OOH network, combining trophy real estate, programmatic technology,
            and creative thinking that punches at the cultural level.
          </p>
        </div>
      </section>

      <section className="relative bg-zinc-950">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <SectionHeading overline="Our mission" title="Make every brand unmissable." />
            </div>
            <div className="md:col-span-7 text-zinc-300 text-lg leading-relaxed space-y-5">
              <p>
                We built Horizn Ad Media on a simple belief that even in a
                digital-first world, nothing matches the impact of a message
                seen in the real world. While out-of-home has often been
                treated as a secondary channel, we saw its true power in the
                way it captures attention on the move, in moments that can't
                be skipped, blocked, or ignored. That insight pushed us to
                approach OOH with greater intent, combining premium inventory
                and thoughtful creatives that respect how people actually
                experience cities.
              </p>
              <p>
                Today, we work with Brands to deliver campaigns that don't just
                show up, but stand out across skylines, commutes, and everyday
                journeys. Bold visibility, backed by measurable impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <SectionHeading
            overline="Why Horizn"
            title="Four reasons brands choose us."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="rounded-2xl border border-white/5 bg-zinc-900/40 p-8 hover:border-blue-500/30 transition-colors duration-500"
                data-testid={`about-pillar-${p.title.toLowerCase().replace(/ /g, "-")}`}
              >
                <p.icon className="h-7 w-7 text-blue-400" />
                <h3 className="mt-5 font-display text-2xl font-bold tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-3 text-zinc-400 leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 md:py-28">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <h3 className="font-display text-3xl md:text-5xl font-bold tracking-tight max-w-2xl">
              Want to see if we're the right fit for your next launch?
            </h3>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="inline-flex items-center gap-2 rounded-full bg-white text-zinc-950 px-7 py-4 text-sm font-bold hover:bg-amber-300 transition-all"
            >
              Talk to us <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
